import { useState } from "react";
import { DOMAIN_META, DEFAULT_PREFERENCES, savePreferences } from "./scoring";
import { ArrowRight, Check } from "lucide-react";

const DOMAIN_ORDER = [
  "Environmental",
  "Labor & Fair Trade",
  "Animal Welfare",
  "LGBTQ+ Equality",
  "Business Type",
  "Country of Origin",
];

export default function OnboardingScreen({ preferences, onSave, isSettings = false }) {
  const [weights, setWeights] = useState({ ...DEFAULT_PREFERENCES.weights, ...preferences?.weights });
  const [toggles, setToggles] = useState({ ...DEFAULT_PREFERENCES.toggles, ...preferences?.toggles });
  const [saved, setSaved] = useState(false);

  function handleSave() {
    const newPrefs = {
      weights,
      toggles,
      onboardingComplete: true,
    };
    savePreferences(newPrefs);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSave(newPrefs);
  }

  return (
    <div style={{
      width: "340px",
      background: "#FFFFFF",
      border: "1px solid var(--border)",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      fontFamily: "Inter, sans-serif",
    }} data-testid="onboarding-screen">

      {/* Header */}
      <div style={{
        padding: "20px 16px 16px",
        borderBottom: "1px solid var(--border)",
        textAlign: "center",
        background: "linear-gradient(135deg, #FFF8F7 0%, #FFFFFF 100%)",
      }}>
        <div style={{
          width: "40px",
          height: "40px",
          background: "#FA7268",
          borderRadius: "12px",
          margin: "0 auto 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
            <path d="M4 10 Q5 6 8 7.5 Q11 9 12 5" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
            <path d="M3 12.5 Q5.5 8 8 10 Q10.5 12 13 7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
          </svg>
        </div>
        <h2 style={{ fontFamily: "Manrope", fontSize: "16px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "4px" }}>
          {isSettings ? "My Values" : "What matters to you?"}
        </h2>
        <p style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
          {isSettings
            ? "Adjust how much each domain affects your score."
            : "Drag to weight what matters most. Toggle off to skip."}
        </p>
      </div>

      {/* Domain sliders */}
      <div style={{ overflowY: "auto", maxHeight: "340px" }}>
        {DOMAIN_ORDER.map((domainName) => {
          const meta = DOMAIN_META[domainName];
          if (!meta) return null;
          const key = meta.key;
          const isOn = toggles[key];
          const isPremium = meta.premium;
          const comingSoon = meta.comingSoon;

          return (
            <div
              key={key}
              data-testid={`domain-setting-${key}`}
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border)",
                opacity: !isOn || comingSoon ? 0.5 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isOn && !comingSoon ? "8px" : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px" }}>{meta.emoji}</span>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "6px" }}>
                      {meta.label}
                      {isPremium && !comingSoon && (
                        <span style={{
                          fontSize: "9px",
                          background: "#FFF8F7",
                          border: "1px solid #FAD7D4",
                          color: "var(--coral)",
                          borderRadius: "4px",
                          padding: "1px 4px",
                          fontWeight: 600,
                        }}>PRO</span>
                      )}
                      {comingSoon && (
                        <span style={{ fontSize: "10px", color: "var(--text-tertiary)", fontStyle: "italic" }}>
                          coming soon
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                      {meta.description}
                    </div>
                  </div>
                </div>

                {!comingSoon && (
                  <button
                    data-testid={`toggle-${key}`}
                    onClick={() => setToggles((t) => ({ ...t, [key]: !t[key] }))}
                    style={{
                      width: "36px",
                      height: "20px",
                      borderRadius: "10px",
                      background: isOn ? "#FA7268" : "#E0E0E0",
                      border: "none",
                      cursor: isPremium ? "not-allowed" : "pointer",
                      position: "relative",
                      transition: "background 0.2s",
                      flexShrink: 0,
                    }}
                    disabled={comingSoon}
                  >
                    <div style={{
                      position: "absolute",
                      top: "2px",
                      left: isOn ? "18px" : "2px",
                      width: "16px",
                      height: "16px",
                      background: "#fff",
                      borderRadius: "50%",
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }} />
                  </button>
                )}
              </div>

              {isOn && !comingSoon && (
                <div className="fade-in" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={weights[key] || 5}
                    data-testid={`slider-${key}`}
                    onChange={(e) => setWeights((w) => ({ ...w, [key]: parseInt(e.target.value) }))}
                    style={{ flex: 1, accentColor: "#FA7268" }}
                  />
                  <span style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "var(--coral)",
                    minWidth: "16px",
                    textAlign: "right",
                  }}>
                    {weights[key] || 5}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save button */}
      <div style={{ padding: "14px 16px", borderTop: "1px solid var(--border)" }}>
        <button
          className="btn-coral"
          onClick={handleSave}
          data-testid="save-preferences-btn"
          style={{ width: "100%", justifyContent: "center", borderRadius: "8px", gap: "6px" }}
        >
          {saved ? (
            <><Check size={14} /> Saved!</>
          ) : (
            <>{isSettings ? "Save changes" : "Done — start shopping"} <ArrowRight size={14} /></>
          )}
        </button>
      </div>
    </div>
  );
}
